import React, { useEffect, memo } from 'react';

import FavoriteIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteFilledIcon from '@mui/icons-material/Favorite';

import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  IconButton,
  Divider,
  Stack,
  Button,
  CardActions,
} from '@mui/material';

import CheckButton from '../CheckButton';
import OnChainButton, { OnChainAction } from '../OnChainButton';
import Marquee from '../Marquee';
import ThemeProvider from '../../theme/ThemeProvider';

import {
  NETWORK_IMAGES,
  ItemFilter,
  getChainInfoByName,
  isNumericString,
  formatImageURL,
} from '../../utils';

export const NFTCard = ({
  chain,
  currency,
  nft: { owner, image, name, categories, description, price, volume, type, version },
  isEnabled,
  isMarketClosed,
  deleteDraft,
  mintNFT,
  listNFT,
  updateNFT,
  onPriceUpdate,
  delistNFT,
  isListedByYou,
  buyNFT,
  isFavorite,
  onToggleFavorite,
  isSeletable,
  isSelected,
  onToggleSelection,
}) => {
  const chainInfo = getChainInfoByName(chain);
  const tokenColor = chainInfo?.color || '#00e5ff';
  const glowColor = chainInfo?.glowColor || 'rgba(0, 229, 255, 0.6)';
  const categoryList = Array.isArray(categories) ? categories.filter(Boolean) : [];

  const handleLikeToggle = (e) => {
    e.stopPropagation();
    onToggleFavorite();
  };

  return (
    <ThemeProvider>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.3s ease-in-out',
          background: isListedByYou ? 'rgba(36, 15, 20, 0.8)' : 'rgba(19, 19, 26, 0.8)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 0 15px rgba(0, 0, 0, 0.5)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 0 20px ${glowColor}`,
            border: `1px solid ${tokenColor}`,
          },
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            component="img"
            // height="200"
            image={formatImageURL(image)}
            alt={name}
            sx={{
              objectFit: 'cover',
              transition: 'transform 0.5s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          />
          {type != ItemFilter.Minted && isSeletable && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                bgcolor: 'rgba(19, 19, 26, 0.7)',
                borderRadius: '50%',
                p: 0.5,
                backdropFilter: 'blur(5px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <CheckButton isSelected={isSelected} toggleSelection={onToggleSelection} />
            </Box>
          )}
          {type != ItemFilter.Minted && onToggleFavorite && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(19, 19, 26, 0.7)',
                borderRadius: '50%',
                p: 0.5,
                backdropFilter: 'blur(5px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <IconButton size="small" onClick={handleLikeToggle}>
                {isFavorite ? (
                  <FavoriteFilledIcon fontSize="small" sx={{ color: '#ff3d71' }} />
                ) : (
                  <FavoriteIcon fontSize="small" sx={{ color: '#fff' }} />
                )}
              </IconButton>
            </Box>
          )}
          {categoryList.length > 0 && (
            <Stack
              direction="row"
              spacing={0.5}
              flexWrap="wrap"
              useFlexGap
              sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                right: 8,
                maxWidth: 'calc(100% - 16px)',
              }}
            >
              {categoryList.map((cat) => (
                <Chip
                  key={cat}
                  label={cat}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(19, 19, 26, 0.85)',
                    color: tokenColor,
                    fontWeight: 'bold',
                    backdropFilter: 'blur(5px)',
                    border: `1px solid ${tokenColor}`,
                    boxShadow: `0 0 10px ${glowColor}`,
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>

        <CardContent sx={{ flexGrow: 1, p: 2 }}>
          <Typography variant="h6" component="div" noWrap sx={{ color: '#fff' }}>
            {name}
            {version !== undefined ? ` (v${version + 1})` : undefined}
          </Typography>
          <Marquee>{description}</Marquee>

          <Stack
            direction="row"
            sx={{ mt: 1, justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ color: tokenColor }}>
                {(type == ItemFilter.MyWallet || type == ItemFilter.Listed) && updateNFT ? (
                  <input
                    type="text"
                    value={price}
                    onChange={(e) => {
                      onPriceUpdate(e.target.value);
                    }}
                    disabled={!isEnabled || isMarketClosed}
                    style={{ color: tokenColor, fontWeight: 'bold' }}
                  />
                ) : (
                  price + ' '
                )}

                <img src={NETWORK_IMAGES[chain]} width={'13px'} />
                {' ' + currency}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                align="right"
                sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
              >
                {volume}
              </Typography>
            </Box>
          </Stack>
        </CardContent>

        <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
        <CardActions sx={{ p: 2 }}>
          {type == ItemFilter.Draft && (
            <OnChainButton
              onClick={mintNFT}
              action={OnChainAction.Mint}
              tokenColor={tokenColor}
              glowColor={glowColor}
              isEnabled={isEnabled}
            />
          )}
          {type == ItemFilter.Draft && (
            <OnChainButton
              onClick={deleteDraft}
              action={OnChainAction.DeleteDraft}
              tokenColor={tokenColor}
              glowColor={glowColor}
              isEnabled={isEnabled}
            />
          )}
          {type == ItemFilter.MyWallet && (
            <OnChainButton
              onClick={listNFT}
              action={OnChainAction.List}
              tokenColor={tokenColor}
              glowColor={glowColor}
              isEnabled={isEnabled && !isMarketClosed && isNumericString(price)}
            />
          )}
          {type == ItemFilter.Listed && updateNFT && (
            <>
              <OnChainButton
                onClick={updateNFT}
                action={OnChainAction.Update}
                tokenColor={tokenColor}
                glowColor={glowColor}
                isEnabled={isEnabled && !isMarketClosed && isNumericString(price)}
              />
              <OnChainButton
                onClick={delistNFT}
                action={OnChainAction.Delist}
                tokenColor={tokenColor}
                glowColor={glowColor}
                isEnabled={isEnabled && !isMarketClosed}
              />
            </>
          )}
          {!isListedByYou && buyNFT && (
            <OnChainButton
              onClick={buyNFT}
              action={OnChainAction.Buy}
              tokenColor={tokenColor}
              glowColor={glowColor}
              isEnabled={isEnabled && !isMarketClosed}
            />
          )}
          {isListedByYou && (
            <OnChainButton
              action={OnChainAction.ListedBuyYou}
              tokenColor={tokenColor}
              glowColor={glowColor}
              isEnabled={false}
            />
          )}
          {type == ItemFilter.Minted && (
            <OnChainButton
              action={OnChainAction.DraftMinted}
              tokenColor={tokenColor}
              glowColor={glowColor}
              isEnabled={false}
            />
          )}
        </CardActions>
      </Card>
    </ThemeProvider>
  );
};

export default memo(NFTCard);
